/**
 * /confirm/:id — AI エージェントの本番の変更（と、テスト用の消す系など・2026-10-08 core/dev-confirm-links）をリンクで確認する画面（2026-09-28 creator/confirm-link）。
 *
 * WHY: 確認のフォーム（elicitation）を出せないエージェント（Codex 等）で「本番に公開して」と頼むと、以前の MCP は断って
 *   ダッシュボードで操作の場所を探させていた（founder の試し）。いまは MCP が本体に「実行せずに登録」させ、このページのリンクを
 *   本人に見せる。本人が押すと、エージェントが同じ要求を 1 回だけ送り直す（apps/mcp/src/pendingActions.ts）。
 *   **Kumo は代わりに実行しない**: 承認は「送り直しを 1 回だけ通す印」（Core・管理の最終判断）。
 *
 * ログインしていなければ App.tsx のログイン前の木が /login へ送り、ログイン後に `state.from` でここへ戻る（今の return path の仕組み）。
 *
 * 守り:
 *   - 画面は本体の GET /v1/developers/pending-actions/:id（kds_）の中身だけで描く。URL から読むのは id だけ（pendingAction.ts readPendingId）
 *   - summary.details の文字列（件名・SQL・理由・返信）とクライアントの名前は外から来た値 = 文に混ぜず別の要素（<code> / <pre> / <strong>）
 *     でテキストとして出す（React が文字として描く = HTML にはならない）。名前は「名乗った名前」と書く（DCR では自己申告）
 *   - ボタンの前に「この操作を頼んだのは、いまあなたが使っている AI エージェントですか？」（管理の依頼）。
 *     「取り消す」（deny）は心当たりの無い依頼を消す道 = メール未確認でも押せる（本体もそう）
 *   - メール未確認は本体が承認を 403 email_unverified で断る。セッションで未確認と分かれば先に案内する（取り消しは出したまま）
 *   - 束（2026-09-29 creator/confirm-bundle）: 本体の bundle があれば、1 回の承認でまとめて通る操作の全部を登録の順に並べる（各行 = 文・状態）。
 *     承認には画面に出した件数（bundleCount）を添え、開いた後に足されていれば 409 pending_bundle_changed → 読み直してもう一度見てもらう。
 *     取り消しも束の全部（ボタンは「やめる」1 つ・2026-10-03 から）
 *   - 見た目（2026-10-03 console/plain-confirm・founder「今何しているかわかりずらすぎる」）: 見出し 1 文 → 結果 1 行（本物の公開先 URL）→
 *     ボタン 2 つ → 安全の 1 行 → ログイン中 →「詳細」（以前の中身の全部）。文の決まりは ../confirmPlain.ts
 *   - 公開先のリンク（2026-10-08 console/publish-url・founder「テストでも本番でも、アプリを公開したときに URL 出してあげると親切かも」）:
 *     押す前は結果の 1 行に URL を文字で（リンクにしない = 確認の途中で離れない）。押した後・もう一度開いたときは「まもなく <URL> で公開」の
 *     開けるリンク（新しいタブ・rel=noopener noreferrer）。URL はその確認の環境（action.environment）の hosting の url = 本体の gameUrls
 *     （独自ドメインがあればそれ）。画面に URL を直書きしない。公開でない確認・URL が読めないときは今までの文
 *   - 押した後（2026-10-15 console/confirm-publishing・founder「パブリッシュした後に『Not published yet』が出てくると不安」）: 公開の確認は
 *     「公開の準備中です」+ 動くバー + 何を待っているか → 済んだら「公開しました」+ 開くボタン / 切れたら「公開されませんでした」+ 次の手
 *     （PublishProgress・../confirmPublishing.ts）。読み直しは 3 秒おき・済んだら止める・12 分で止める
 *   - クリックジャッキング: public/_headers の frame-ancestors 'none' / X-Frame-Options: DENY が全ページに効く（消さない）
 */
import { Fragment, useEffect, useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { EmailUnverifiedNotice } from '../components/EmailUnverified';
import { Spinner } from '../components/ui';
import { useResource } from '../hooks';
import { errorText, formatDateTime, useT, type MessageKey } from '../i18n';
import {
  APPROVED_POLL_MS,
  approveBody,
  bundleRows,
  clientName,
  cliStoppedWaiting,
  describeAction,
  isBundleChanged,
  isCliClient,
  isNotWaiting,
  liveStatus,
  nextStepKey,
  readPendingId,
  statusAfterError,
  wrongAccountOf,
  statusMessageKey,
  statusOf,
  type ActionDescription,
  type PendingActionResponse,
  type PendingStatus
} from '../pendingAction';
import { approvePendingAction, denyPendingAction, pendingActionPath } from '../pendingActionApi';
import { envPath, type ApiError } from '../api';
import { liveUrlOf, plainSummary, publishStateLine, publishTarget, type PlainSummary } from '../confirmPlain';
import { POLL_LIMIT_MS, publishingView, type PublishingView } from '../confirmPublishing';
import { useSession } from '../session';
import { AuthShell } from './AccountRecovery';

export function ConfirmActionPage() {
  const { t } = useT();
  const params = useParams();
  const id = readPendingId(params.id);
  const res = useResource<PendingActionResponse>(id ? pendingActionPath(id) : null);

  if (!id) return <Message kind="error" text={t('confirmAction.invalidLink')} />;
  if (res.loading && !res.data) {
    return (
      <AuthShell title={t('confirmPlain.pageTitle')}>
        <Spinner label={t('common.loading')} />
      </AuthShell>
    );
  }
  if (res.error?.code === 'route_missing') return <Message kind="error" text={t('confirmAction.notAvailable')} />;
  // 別のアカウントのリンク（本体の 403 wrong_account）: 「期限切れ」より先に見る（以前はここで期限切れの文になっていた・2026-10-08 founder）
  const wrong = wrongAccountOf(res.error);
  if (wrong) return <WrongAccount owner={wrong.email} />;
  const gone = statusAfterError(res.error);
  if (gone) return <StatusMessage status={gone} />;
  if (res.error || !res.data?.action) return <Message kind="error" text={res.error ? errorText(res.error) : t('confirmAction.invalidLink')} />;
  const status = statusOf(res.data.action.status);
  // 束をもう一度開いたとき（承認の後）も件数つきの文にする。公開の確認なら公開先のリンク（DoneMessage）
  if (status !== 'pending') return <DoneMessage id={id} status={status} count={bundleRows(res.data)?.length} data={res.data} />;
  // パソコンの CLI がもう待っていない（2026-10-15 core/confirm-done）: 押しても何も起きない = ボタンを出さずにそう言う（NotWaiting）
  if (cliStoppedWaiting(res.data.action)) return <NotWaiting publish={!!publishTarget(res.data)} />;
  return <ConfirmForm id={id} data={res.data} reload={res.reload} />;
}

function Message({ kind, text, next, state }: { kind: 'error' | 'ok' | 'info'; text: string; next?: string | null; state?: StateLine }) {
  const { t } = useT();
  const cls = kind === 'error' ? 'alert alert-error' : kind === 'ok' ? 'alert alert-ok' : 'alert';
  return (
    <AuthShell title={t('confirmPlain.pageTitle')}>
      <PublishState state={state} />
      <p className={cls} role={kind === 'error' ? 'alert' : 'status'}>
        {text}
      </p>
      <NextStep next={next} />
    </AuthShell>
  );
}

type StateLine = ReturnType<typeof publishStateLine>;

/**
 * 公開されたかの 1 行（confirmPlain.ts publishStateLine・2026-10-13 console/confirm-wait）。ページの一番上に、一番目立つ形（大きな太字・色の帯）で。
 * 押す前 =「まだ公開されていません。下のボタンを押すと公開されます」→ 押した後・済んだ・切れた で文と色が変わる。
 * role=status = 押した後に状態が変わったとき（3 秒おきの読み直し）も読み上げで伝わる。公開でない確認は null = 出さない
 */
function PublishState({ state }: { state?: StateLine }) {
  const { t } = useT();
  if (!state) return null;
  return (
    <p className={`confirm-plain-state confirm-plain-state-${state.tone}`} role="status">
      {t(state.key as MessageKey)}
    </p>
  );
}

/**
 * 承認の後に本人がすること（pendingAction.ts nextStepKey・2026-10-13 core/confirm-flow）。AI の接続 =「済んだと言わなければチャットに戻って『押した』と送る」・
 * CLI =「自分で続ける・閉じてよい」。承認の文（緑）とは別の、太字の 1 行 = 見落とされないように（Web の Claude では押しても何か送るまで進まない）
 */
function NextStep({ next }: { next?: string | null }) {
  const { t } = useT();
  if (!next) return null;
  return (
    <p className="confirm-plain-safety" role="note">
      <strong>{t(next as MessageKey)}</strong>
    </p>
  );
}

/**
 * 別のアカウントで開いたリンク（2026-10-08 console/confirm-wrong-account・founder「期限切れではないのに期限切れと読めて、何をすればいいか分からない」）。
 * 文 =「別の KUMODeck アカウント（r•••@k•••.com）のリンクです。いまは <今のメール> でログイン中。ログアウトして、そのアカウントで入り、もう一度開いて」。
 * ボタン =「ログアウトして切り替える」: signOut → App.tsx のログイン前の木が /login へ送る（state.from = この /confirm/:id）→ 入った後にここへ戻る
 * （今の return path の仕組み = 新しい道は作らない）。中身（何を変えるか・プロジェクト）は描かない = 本体も返さない（伏せ字のメールだけ）。
 * 押せる物はログアウトだけ（別の人の確認を押す道は無い・本体の承認 / 拒否も 404 のまま）
 */
function WrongAccount({ owner }: { owner: string }) {
  const { t, tr } = useT();
  const { developer, signOut } = useSession();
  const [busy, setBusy] = useState(false);
  return (
    <AuthShell title={t('confirmPlain.pageTitle')}>
      <p className="alert" role="status">
        {tr('confirmAction.wrongAccount', { owner: <strong>{owner || '•••'}</strong>, email: <strong>{developer?.email ?? ''}</strong> })}
      </p>
      <div className="confirm-plain-actions">
        <button
          type="button"
          className="btn btn-primary confirm-plain-btn"
          disabled={busy}
          onClick={() => {
            setBusy(true);
            void signOut();
          }}
        >
          {busy ? t('common.working') : t('confirmAction.switchAccount')}
        </button>
      </div>
    </AuthShell>
  );
}

/**
 * 決まった後の状態（押した後・もう一度開いたとき）。approved は「許可しました」+ 次にすること（next・NextStep）
 * （束は件数つき・statusMessageKey。2026-09-29 creator/prod-feel の「自動で続けます。閉じてかまいません」は 2026-10-13 core/confirm-flow でやめた）
 */
function StatusMessage({ status, count, next, state }: { status: PendingStatus; count?: number; next?: string | null; state?: StateLine }) {
  const { t } = useT();
  const kind = status === 'approved' || status === 'used' ? 'ok' : status === 'denied' ? 'info' : 'error';
  const m = statusMessageKey(status, count);
  return <Message kind={kind} text={t(m.key as MessageKey, m.vars)} next={next} state={state} />;
}

/**
 * パソコンの CLI がもう待っていない確認（2026-10-15 core/confirm-done・10/15 Mega Strike・pendingAction.ts cliStoppedWaiting の WHY）。
 * 見出し =「ここで押しても、もう何も起きません」・何が起きたか（CLI が待つのをやめた・何も変わっていない）・次の手（同じコマンドをもう一度 → 新しいページで押す）。
 * ボタンは出さない（本体も承認を 409 pending_action_not_waiting で断る）。取り消しも要らない（送り直す CLI がいない = 期限で消える）
 */
function NotWaiting({ publish }: { publish: boolean }) {
  const { t } = useT();
  return (
    <AuthShell title={t('confirmPlain.notWaiting.title')}>
      <p className="alert alert-error" role="alert">
        {t(publish ? 'confirmPlain.notWaiting.linePublish' : 'confirmPlain.notWaiting.line')}
      </p>
      <p className="confirm-plain-safety" role="note">
        <strong>{t(publish ? 'confirmPlain.notWaiting.nextPublish' : 'confirmPlain.notWaiting.next')}</strong>
      </p>
    </AuthShell>
  );
}

/**
 * 確認の環境（本体の action.environment）。hosting の URL を読む先・「詳細」の環境の欄。知らない値は本番（閉じる側 = 強い注意の方）。
 * development は 2026-10-08 core/dev-confirm-links から（テスト用の消す系・BAN など）
 */
const envOf = (action: PendingActionResponse['action']): 'development' | 'production' => (action.environment === 'development' ? 'development' : 'production');

/**
 * 決まった後の画面。公開の確認（publishTarget）は PublishProgress（準備中 → 公開しました / 公開されませんでした・confirmPublishing.ts）、
 * それ以外は StatusMessage（今までの文）。liveUrl = 押す前に読んだ公開先の URL（あれば読み直さない）・undefined = ここで本体の hosting を読む
 * （もう一度開いたとき）。読み中・読めない = URL なしの文（リンクは添え物 = 失敗で止めない・2026-10-08 publish-url）
 */
function DoneMessage({ id, status: decided, count, data, liveUrl }: { id: string; status: PendingStatus; count?: number; data: PendingActionResponse; liveUrl?: string }) {
  // 押した後は approved の間だけ本体を読み直す（2026-10-13 core/confirm-flow・pendingAction.ts liveStatus）。
  // 送り直しが済めば used（公開なら「公開しました」+ 開くボタン）に変わる = 本人がチャットに戻る要るかが画面で分かる。決まった後は読まない
  const [status, setStatus] = useState<PendingStatus>(decided);
  // 読み直しで分かった「CLI がもう待っていない」（view が読み直しの有無を決める = fresh より前に要る → 状態に持つ）
  const [cliGone, setCliGone] = useState(false);
  // 読み直しの上限と「時間がかかっています」の時計（2026-10-15 console/confirm-publishing）。起点 = この画面が出た時（押した時・開いた時）。
  // WHY 本体の decidedAt を使わないか: 端末の時計のずれで、開いた瞬間に「上限を過ぎた」と出るのを避ける（この画面でどれだけ待ったかで十分）
  const [startedAt] = useState(() => Date.now());
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (status !== 'approved') return;
    const h = window.setInterval(() => setNow(Date.now()), 5000);
    return () => window.clearInterval(h);
  }, [status]);
  const cli = isCliClient(data.action);
  const target = publishTarget(data);
  const project = data.action.project;
  const read = target && project && liveUrl === undefined && (status === 'approved' || status === 'used');
  const hosting = useResource<{ url?: unknown }>(read ? `${envPath(project.id, envOf(data.action))}/hosting` : null);
  const url = liveUrl ?? liveUrlOf(hosting.data?.url);
  const elapsedMs = now - startedAt;
  // 押した後に CLI が止まった（読み直しの中身の waiter・2026-10-15 core/confirm-done）= 「準備中」を続けず「公開されませんでした」
  const view = publishingView({ status, target, cli, elapsedMs, hasUrl: !!url, cliGone });
  // 読み直すのは approved の間・上限まで（公開でない確認も同じ上限 = ずっと読み続けない）
  const polling = status === 'approved' && (view ? view.poll : elapsedMs < POLL_LIMIT_MS);
  const fresh = useResource<PendingActionResponse>(polling ? pendingActionPath(id) : null, APPROVED_POLL_MS);
  useEffect(() => {
    setStatus((s) => liveStatus(s, fresh.data));
    // 一度分かったら戻さない（stopped で読み直しを止めると fresh.data が空に戻りうる = 表示が行き来しない）
    setCliGone((g) => g || cliStoppedWaiting(fresh.data?.action));
  }, [fresh.data]);
  if (view) return <PublishProgress view={view} url={url} />;
  return <StatusMessage status={status} count={count} next={nextStepKey(status, cli)} />;
}

/**
 * 公開の確認の押した後（2026-10-15 console/confirm-publishing・founder「公開準備中ですみたいな表示とアニメーションを」・confirmPublishing.ts の WHY）。
 * 見出し = いまの段（公開の準備中です / 公開しました / 公開されませんでした）。下の枠 = 動くバー（準備中だけ）+ 何を待っているか / どこで開けるか /
 * 何が起きたか。終わったら「アプリを開く」の大きなボタン。枠は role=status = 段が変わったら読み上げでも伝わる。
 * URL は liveUrlOf を通った http(s) だけ = javascript: 等はリンクにならない。開いた先から確認の画面を操作させない（noopener）
 */
function PublishProgress({ view, url }: { view: PublishingView; url: string | null }) {
  const { t, tr } = useT();
  const link = (u: string) => (
    <a href={u} target="_blank" rel="noopener noreferrer" className="confirm-plain-url">
      {u}
    </a>
  );
  const moving = view.phase === 'working' || view.phase === 'slow';
  const vars = { minutes: POLL_LIMIT_MS / 60_000 };
  return (
    <AuthShell title={t(view.title as MessageKey)}>
      <div className={`confirm-publishing confirm-publishing-${view.phase}`} role="status">
        {moving && <span className="confirm-publishing-bar" aria-hidden="true" />}
        <p className="confirm-publishing-line">{url ? tr(view.line as MessageKey, { ...vars, url: link(url) }) : t(view.line as MessageKey, vars)}</p>
        {/* 準備中も行き先は見せる（終わったらここで開ける・押す前の結果の 1 行と同じ URL） */}
        {moving && url && <p className="confirm-publishing-where small muted">{tr('confirmPlain.publishing.where', { url: link(url) })}</p>}
      </div>
      {view.open && url && (
        <a className="btn btn-primary confirm-plain-btn" href={url} target="_blank" rel="noopener noreferrer">
          {t(view.open as MessageKey)}
        </a>
      )}
      {/* 上限まで待っても知らせが無い: 読み直しは止めた（confirmPublishing.ts POLL_LIMIT_MS）= 本人が押して今の状態を読む */}
      {view.phase === 'unknown' && (
        <button type="button" className="btn btn-primary confirm-plain-btn" onClick={() => window.location.reload()}>
          {t('confirmPlain.publishing.reload')}
        </button>
      )}
      {view.next && (
        <p className="confirm-plain-safety" role="note">
          <strong>{t(view.next as MessageKey)}</strong>
        </p>
      )}
    </AuthShell>
  );
}

/**
 * 何が起きるかの文と補足（1 件分）。外から来た値は <code> / <pre> でテキストのまま（文の中の「指示」に見えないように）。
 * 束のときは 1 行ずつこれを並べる（どの行も 1 件のときと同じ文 = describeAction の守り〔隠す機能を出さない等〕がそのまま効く）
 */
function Description({ desc }: { desc: ActionDescription }) {
  const { t, tr } = useT();
  const vars: Record<string, ReactNode> = Object.fromEntries(Object.entries(desc.vars).map(([k, v]) => [k, <code key={k}>{v}</code>]));
  return (
    <>
      {/* 公開中のサイトを止める承認（config.push の allowTurnOff・AGT-16）: 文より先に赤で言う。機能の名前は本体の値 = <code> でテキストとして */}
      {desc.warning && (
        <p className="alert alert-error" role="alert">
          {tr(desc.warning.key as MessageKey, Object.fromEntries(Object.entries(desc.warning.vars).map(([k, v]) => [k, <code key={k}>{v}</code>])))}
        </p>
      )}
      <p className="confirm-action-what">{tr(desc.key as MessageKey, vars)}</p>
      {desc.extras.length > 0 && (
        <dl className="kv confirm-action-kv">
          {desc.extras.map((x) => (
            <div key={x.key} className="confirm-action-row">
              <dt>{t(x.key as MessageKey)}</dt>
              <dd>{x.block ? <pre className="confirm-action-block">{x.value}</pre> : <code>{x.value}</code>}</dd>
            </div>
          ))}
        </dl>
      )}
    </>
  );
}

/**
 * 押す前に「何が変わるか」を見せる行（2026-10-25 security/prompt-lava-fixes の D・点検 docs/security/2026-10-25-prompt-lava-review.md 2-⑥・confirmPlain.ts の files / sameAsTest / hosts）。
 * WHY: 以前の画面は「公開しますか？」+ URL（と「サーバーの更新も」）だけ = 中の指示で入った悪いファイルや、データを外へ送るコードを、人は
 *   「いつもの更新」と見分けられなかった。結び付け（見た物 = 通る物）は本体が守る（本文・版の前提）= ここは見せる物を厚くするだけ。
 *   1. ファイルの差: いま公開中のものと比べた数 + 先頭の数件の名前（全部は「詳細」）。初めての公開は数だけ
 *   2. テスト用の URL: 押す前に開いて試せるリンク（新しいタブ・noopener）。中身が違うと分かれば注意の色で言う（試しても確かめられない）
 *   3. サーバーの更新に書かれた外のアドレス: 注意の色で並べ、知らなければ「やめる」と言う
 * 外から来た値（ファイル名・アドレス）は <code> のテキスト（React が文字として描く = HTML にならない・ほかの行と同じ守り）。
 * URL は liveUrlOf を通った http(s) だけ = javascript: 等はリンクにならない
 */
function ReviewLines({ plain, testUrl }: { plain: PlainSummary; testUrl: string | null }) {
  const { t, tr } = useT();
  const codes = (xs: string[]) => xs.map((x, i) => (
    <Fragment key={`${i}:${x}`}>
      {i > 0 && t('confirmPlain.listJoin')}
      <code>{x}</code>
    </Fragment>
  ));
  const f = plain.files;
  const rows: Array<[label: string, names: string[], count: number]> = f
    ? [
        ['confirmPlain.files.addedLabel', f.names.added, f.added],
        ['confirmPlain.files.changedLabel', f.names.changed, f.changed],
        ['confirmPlain.files.removedLabel', f.names.removed, f.removed]
      ]
    : [];
  return (
    <>
      {f && (
        <div className="confirm-plain-files small">
          <p>
            {f.first
              ? t('confirmPlain.files.first', { count: f.added })
              : f.added + f.changed + f.removed === 0
                ? t('confirmPlain.files.same')
                : t('confirmPlain.files.changed', { added: f.added, changed: f.changed, removed: f.removed })}
          </p>
          {!f.first && rows.some(([, names]) => names.length > 0) && (
            <ul>
              {rows
                .filter(([, names]) => names.length > 0)
                .map(([label, names, count]) => (
                  <li key={label}>
                    {t(label as MessageKey)} {codes(names)}
                    {count > names.length && <> {t('confirmPlain.files.more', { count: count - names.length })}</>}
                  </li>
                ))}
            </ul>
          )}
        </div>
      )}
      {testUrl && f && (
        <p className="confirm-plain-test small">
          {tr('confirmPlain.testLink', {
            url: (
              <a href={testUrl} target="_blank" rel="noopener noreferrer" className="confirm-plain-url">
                {testUrl}
              </a>
            )
          })}
          {plain.sameAsTest === true && <>{t('confirmPlain.sentenceGap')}{t('confirmPlain.testSame')}</>}
        </p>
      )}
      {f && plain.sameAsTest === false && (
        <p className="alert alert-warn" role="note">
          {t('confirmPlain.testDiffers')}
        </p>
      )}
      {plain.hosts && (
        <p className="alert alert-warn confirm-plain-hosts" role="note">
          <span>
            {tr('confirmPlain.hosts', {
              list: (
                <>
                  {codes(plain.hosts.names)}
                  {plain.hosts.more > 0 && t('confirmPlain.hostsMore', { count: plain.hosts.more })}
                </>
              )
            })}
          </span>
        </p>
      )}
    </>
  );
}

function ConfirmForm({ id, data, reload }: { id: string; data: PendingActionResponse; reload: () => void }) {
  const { t, tr } = useT();
  const { developer, signOut } = useSession();
  const action = data.action;
  // 束（2026-09-29 creator/confirm-bundle）: 1 回の承認でまとめて通る操作の全部を登録の順に見せる。ボタンの名前は最後の操作
  // （初めての本番の公開 = 「公開（hosting）を ON」→「版 N を公開」→ ボタンは「公開する」= MCP が本人に言う名前と同じ）
  const rows = bundleRows(data);
  const desc = rows ? rows[rows.length - 1]!.desc : describeAction(action);
  const [busy, setBusy] = useState<'approve' | 'deny' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [unverified, setUnverified] = useState(developer?.emailVerified === false);
  const [done, setDone] = useState<PendingStatus | null>(null);
  // 押したら本体が「CLI がもう待っていない」で断った（ページを開いた後に CLI が止まった・2026-10-15 core/confirm-done）
  const [notWaiting, setNotWaiting] = useState(false);
  // 公開先の URL（「https://… で誰でも遊べるようになります」に出す本物の URL）。本番の hosting を本人のセッションで読むだけ。
  // 読めない（古い本体・まだ URL が無い）ときは URL なしの文 = 確認そのものは止めない
  // 2026-10-08 publish-url: 確認の環境の hosting（テスト用への公開ならテスト用の URL）
  const hosting = useResource<{ url?: unknown }>(action.project ? `${envPath(action.project.id, envOf(action))}/hosting` : null);
  const liveUrl = liveUrlOf(hosting.data?.url);
  // 本番の公開の確認だけ: テスト用（development）の URL も読む = 押す前にテスト用で試せる（2026-10-25 security/prompt-lava-fixes の D・ReviewLines）。
  // 読めない・テスト用に何も公開していない = 行を出さない（確認そのものは止めない）
  const testHosting = useResource<{ url?: unknown }>(action.project && publishTarget(data) === 'live' ? `${envPath(action.project.id, 'development')}/hosting` : null);
  const testUrl = liveUrlOf(testHosting.data?.url);

  // 押した後: 公開の確認なら、押す前に読んだ URL を開けるリンクで（無ければ DoneMessage が読み直す）
  if (notWaiting) return <NotWaiting publish={!!publishTarget(data)} />;
  if (done) return <DoneMessage id={id} status={done} count={rows?.length} data={data} liveUrl={liveUrl ?? undefined} />;

  const name = clientName(action);
  const email = developer?.email ?? '';

  const fail = (err: unknown) => {
    const e = err as ApiError;
    if (e?.code === 'email_unverified') {
      setUnverified(true);
      return;
    }
    // 開いた後に束に操作が足された（本体の 409 pending_bundle_changed）: 読み直して、足された物も見てから押してもらう
    if (isNotWaiting(e)) {
      setNotWaiting(true);
      return;
    }
    if (isBundleChanged(e)) {
      setNotice(t('confirmAction.bundleChanged'));
      reload();
      return;
    }
    const next = statusAfterError(e);
    if (next) setDone(next);
    else setError(errorText(e));
  };

  const decide = async (which: 'approve' | 'deny') => {
    setError(null);
    setNotice(null);
    setBusy(which);
    try {
      // 束は画面に出した件数を添える（approveBody）。拒否は束の全部に効く（件数は要らない = 断る向き）
      if (which === 'approve') await approvePendingAction(id, approveBody(rows));
      else await denyPendingAction(id);
      setDone(which === 'approve' ? 'approved' : 'denied');
    } catch (err) {
      fail(err);
    } finally {
      setBusy(null);
    }
  };

  const project = action.project;
  const cli = isCliClient(action);
  // テスト用（development）の確認（2026-10-08 core/dev-confirm-links）。「詳細」の環境の欄と前置きを本番と言わない文に
  const test = envOf(action) === 'development';
  // 見える所（見出し・結果）は利用者の言葉で（confirmPlain.ts の WHY・founder 2026-10-03）。公開先の URL は本体の hosting から読んだ本物
  const plain = plainSummary(data, { projectName: project?.name ?? null, liveUrl });
  // 外から来た値（件名・ドメイン・プレイヤー ID・秘密の名前）は見出しの文に混ぜず <code> でテキストとして（以前の Description と同じ守り）。
  // プロジェクトの名前は本人が付けた値 = <strong>。URL は本体が返した値 = <strong>（リンクにしない = 確認の途中で離れない）
  const wrap = (vars: Record<string, string>): Record<string, ReactNode> =>
    Object.fromEntries(
      Object.entries(vars).map(([k, v]) => [k, k === 'name' ? <strong key={k}>{v}</strong> : k === 'url' ? <strong key={k} className="confirm-plain-url">{v}</strong> : k === 'count' ? v : <code key={k}>{v}</code>])
    );
  // 「セーブ、ランキングとマルチプレイ」= 最後の 2 つの間だけ listLast（2026-10-04 plain-confirm-2: 「・」でつなぐと箇条書きに見えて不自然）
  const items = plain.alsoOn.map((f) => t(`confirmPlain.feature.${f}` as MessageKey));
  const joinList = (xs: string[]) => (xs.length > 1 ? xs.slice(0, -1).join(t('confirmPlain.listJoin')) + t('confirmPlain.listLast') + xs[xs.length - 1] : (xs[0] ?? ''));
  const also = joinList(items);
  // 同じ 1 回の確認でいっしょにすること（サーバーの開始・更新・設定・独自のアドレス。2026-10-08 core/one-confirm・confirmPlain.ts alsoDo）
  const alsoDo = joinList(plain.alsoDo.map((k) => t(`confirmPlain.alsoDo.${k}` as MessageKey)));
  // 表の名前（SQL に書かれたまま = 外から来た値）は <strong> のテキスト（React が文字として出す = HTML にならない）。
  // <code> の枠は文の「」と重なって読みにくかった（2026-10-04 の写真）。つなぎ（」「 / ", "）は言語ごと = 文の「」の中に並ぶ
  const result = { ...wrap(plain.result.vars), ...Object.fromEntries(Object.entries(plain.result.lists ?? {}).map(([k, v]) => [k, <strong key={k}>{v.join(t('confirmPlain.tableJoin'))}</strong>])) };
  return (
    <AuthShell title={tr(plain.headline.key as MessageKey, wrap(plain.headline.vars))}>
      {/* 公開の確認: 一番上に「まだ公開されていません。下のボタンを押すと公開されます」（2026-10-13 console/confirm-wait・PublishState） */}
      <PublishState state={publishStateLine('pending', publishTarget(data), cli)} />
      {/* 公開中のゲームを止める（AGT-16）: 結果より先に赤で 1 行（機能の名前は「詳細」の中の注意に） */}
      {plain.stopsGame && (
        <p className="alert alert-error" role="alert">
          {t('confirmPlain.stopsGame')}
        </p>
      )}
      {/* 公開の束の中のデータの保存形式の変更で、保存済みのデータが消える・読めない（2026-10-15 cli/one-confirm・confirmPlain.ts dataWarning）。
          単発の確認と同じ文を、止める（stopsGame）と同じ赤で結果の前に = 1 回にまとめても見ずに押させない */}
      {plain.dataWarning && (
        <p className="alert alert-error" role="alert">
          {tr(plain.dataWarning.key as MessageKey, { ...wrap(plain.dataWarning.vars), ...Object.fromEntries(Object.entries(plain.dataWarning.lists ?? {}).map(([k, v]) => [k, <strong key={k}>{v.join(t('confirmPlain.tableJoin'))}</strong>])) })}
        </p>
      )}
      <p className="confirm-plain-result">
        {tr(plain.result.key as MessageKey, result)}
        {also && <>{t('confirmPlain.sentenceGap')}{t('confirmPlain.alsoOn', { list: also })}</>}
        {alsoDo && <>{t('confirmPlain.sentenceGap')}{t('confirmPlain.alsoDo', { list: alsoDo })}</>}
        {plain.more && <>{t('confirmPlain.sentenceGap')}{t('confirmPlain.otherChanges')}</>}
        {/* テスト用（development）の確認: どちらのアプリかを結果の後に 1 文（2026-10-08 core/dev-confirm-links・confirmPlain.ts testApp の WHY） */}
        {plain.testApp && <>{t('confirmPlain.sentenceGap')}{t('confirmPlain.testOnly')}</>}
      </p>
      {/* 公開の費用の目安（2026-10-04 money/publish-cost）: 結果の 1 行の下に小さく 1 行。本体が目安を出せなかった時は出さない */}
      {/* 2026-10-07 追記 7（管理の判断）: 目安の行の後ろにいつも「Prices are at cost, including the tax our providers add.」。
          WHY 率で出し分けないか: 税は請求書に税が載る事業者の項目だけ = 率は項目ごとに違う。「10% consumption tax」は利用者に税を課すように読める */}
      {plain.cost && (
        <p className="confirm-plain-cost small muted">
          {t('confirmPlain.cost', plain.cost)}
          {t('confirmPlain.sentenceGap')}
          {t('money.pricesFromProviders')}
        </p>
      )}
      {/* 何が変わるか（ファイルの差・テスト用の URL・サーバーの更新に書かれた外のアドレス）。2026-10-25 security/prompt-lava-fixes の D */}
      <ReviewLines plain={plain} testUrl={testUrl} />
      {unverified && <EmailUnverifiedNotice>{t('confirmAction.emailUnverified', { email })}</EmailUnverifiedNotice>}
      {notice && (
        <p className="alert" role="status">
          {notice}
        </p>
      )}
      {error && (
        <p className="alert alert-error" role="alert">
          {error}
        </p>
      )}
      {/* 大きなボタン 2 つ（する / やめる）。押せる条件・送る中身は以前のまま（承認 = approveBody・やめる = 束の全部を deny） */}
      <div className="confirm-plain-actions">
        <button type="button" className="btn btn-primary confirm-plain-btn" disabled={busy !== null || unverified} onClick={() => void decide('approve')}>
          {busy === 'approve' ? t('common.working') : t(desc.button as MessageKey)}
        </button>
        <button type="button" className="btn confirm-plain-btn" disabled={busy !== null} onClick={() => void decide('deny')}>
          {busy === 'deny' ? t('common.working') : t('confirmPlain.cancel')}
        </button>
      </div>
      {/* 安全の 1 行（以前の「頼んだのはあなたのエージェントか」の代わり・管理の依頼の守り）。CLI からの要求は「自分のパソコンで実行したか」 */}
      <p className="confirm-plain-safety" role="note">
        <strong>{t(cli ? 'confirmPlain.safetyCli' : 'confirmPlain.safety')}</strong>
      </p>
      <p className="small muted">
        {tr('confirmAction.signedInAs', { email: <strong>{email}</strong> })}{' '}
        <button type="button" className="link-button" onClick={() => void signOut()}>
          {t('confirmAction.notYou')}
        </button>
      </p>

      {/* 詳細: 以前の画面の中身の全部（文・補足・束の順・プロジェクト・本番・頼んだ AI・期限・承認の仕組み）。隠すのではなく畳むだけ */}
      <details className="confirm-plain-details">
        <summary>{t('confirmPlain.details')}</summary>
        {/* 束は本番の公開だけ（本体が development の物を束に入れない）= テスト用の前置きは 1 件のときだけ */}
        <p className="muted">{rows ? t('confirmAction.leadBundle', { count: rows.length }) : t(test ? 'confirmAction.leadTest' : 'confirmAction.lead')}</p>
        {rows ? (
          <>
            <h3 className="mcp-auth-title">{t('confirmAction.whatBundle', { count: rows.length })}</h3>
            <ol className="confirm-action-bundle">
              {rows.map((r) => (
                <li key={r.position}>
                  <Description desc={r.desc} />
                  {r.status !== 'pending' && <span className="pill">{t(`confirmAction.rowStatus.${r.status}` as MessageKey)}</span>}
                </li>
              ))}
            </ol>
          </>
        ) : (
          <>
            <h3 className="mcp-auth-title">{t('confirmAction.what')}</h3>
            <Description desc={desc} />
          </>
        )}
        <dl className="kv confirm-action-kv">
          <div className="confirm-action-row">
            <dt>{t('confirmAction.project')}</dt>
            <dd>{project ? tr('confirmAction.projectValue', { name: <strong>{project.name}</strong>, slug: <code>{project.slug}</code> }) : t('confirmAction.projectUnknown')}</dd>
          </div>
          <div className="confirm-action-row">
            <dt>{t('confirmAction.environment')}</dt>
            <dd>
              {test ? <span className="pill">{t('confirmAction.testApp')}</span> : <span className="pill pill-warn">{t('confirmAction.production')}</span>}
            </dd>
          </div>
          <div className="confirm-action-row">
            <dt>{t('confirmAction.client')}</dt>
            {/* CLI（xkumo connect / ブラウザの xkumo login）からの要求: 名前は本体が決めた "Kumo CLI" = 自己申告ではない。エージェントが動かしているかもしれない、を書く */}
            <dd>{cli ? t('confirmAction.clientCli') : tr('confirmAction.clientValue', { name: <strong>{name ?? t('confirmAction.unnamed')}</strong> })}</dd>
          </div>
        </dl>
        {action.expiresAt && <p className="small muted">{t('confirmAction.expires', { time: formatDateTime(action.expiresAt) })}</p>}
        <p className="small muted">{t(rows ? 'confirmAction.cancelNoteBundle' : 'confirmAction.cancelNote')}</p>
        <p className="small muted">{t(rows ? 'confirmAction.howItWorksBundle' : 'confirmAction.howItWorks')}</p>
      </details>
    </AuthShell>
  );
}
